import type { ChainId } from '@/sdk'
import { Currency, JSBI, Percent, Token, Trade } from '@/sdk'

import type { PairCatalogEntry } from '@/data/marketApi/types'
import { pairCatalogKey as productPairCatalogKey, toCatalogAddress } from '@sirius-cybernetics-42/product-config'
import { BLOCKED_PRICE_IMPACT_NON_EXPERT } from '@/constants'

export const SWAP_DISABLED_ERROR = 'Swapping this token is not available'
export const LIQUIDITY_DISABLED_ERROR = 'Liquidity operations are not available for this pair'

type LiquidityOperation = 'add' | 'remove'

type TokenPolicy = {
  swapEnabled: boolean
  addLiquidityEnabled: boolean
  removeLiquidityEnabled: boolean
}

const tokenPolicyByChainAndAddress = new Map<ChainId, Map<string, TokenPolicy>>()
const pairPolicyByChainAndKey = new Map<ChainId, Map<string, PairCatalogEntry>>()

function registerTokenPolicy(chainId: ChainId, address: string, policy: TokenPolicy): void {
  let byAddress = tokenPolicyByChainAndAddress.get(chainId)
  if (!byAddress) {
    byAddress = new Map()
    tokenPolicyByChainAndAddress.set(chainId, byAddress)
  }
  byAddress.set(address.toLowerCase(), policy)
}

export const pairCatalogKey = productPairCatalogKey

export function syncTokenPolicyFromCatalog(
  chainId: ChainId,
  entries: Array<{
    catalogAddress?: string
    onChainAddress?: string
    swapEnabled: boolean
    addLiquidityEnabled?: boolean
    removeLiquidityEnabled?: boolean
    role?: string
  }>,
): void {
  for (const entry of entries) {
    if (entry.role === 'wrapped_native') continue
    const address = toCatalogAddress(entry.catalogAddress ?? entry.onChainAddress ?? '')
    if (entry.role === 'native' || entry.role === 'erc20' || entry.role === 'stablecoin' || entry.role === undefined) {
      registerTokenPolicy(chainId, address, {
        swapEnabled: entry.swapEnabled,
        addLiquidityEnabled: entry.addLiquidityEnabled ?? true,
        removeLiquidityEnabled: entry.removeLiquidityEnabled ?? true,
      })
    }
  }
}

export function syncPairPolicyFromCatalog(chainId: ChainId, entries: PairCatalogEntry[]): void {
  let byKey = pairPolicyByChainAndKey.get(chainId)
  if (!byKey) {
    byKey = new Map()
    pairPolicyByChainAndKey.set(chainId, byKey)
  }
  byKey.clear()
  for (const entry of entries) {
    byKey.set(pairCatalogKey(entry.token0Address, entry.token1Address), entry)
  }
}

export function syncMarketPolicyFromCatalogs(
  chainId: ChainId,
  tokenEntries: Array<{
    onChainAddress?: string
    swapEnabled: boolean
    addLiquidityEnabled?: boolean
    removeLiquidityEnabled?: boolean
    role?: string
  }>,
  pairEntries?: PairCatalogEntry[],
): void {
  syncTokenPolicyFromCatalog(chainId, tokenEntries)
  if (pairEntries) syncPairPolicyFromCatalog(chainId, pairEntries)
}

function catalogAddressForToken(token: Token): string {
  return toCatalogAddress(token.address)
}

function getTokenPolicy(chainId: ChainId | undefined, token: Token): TokenPolicy | undefined {
  if (!chainId || token.chainId !== chainId) return undefined
  return tokenPolicyByChainAndAddress.get(chainId)?.get(catalogAddressForToken(token))
}

function getPairPolicy(
  chainId: ChainId | undefined,
  tokenA: Token,
  tokenB: Token,
): PairCatalogEntry | undefined {
  if (!chainId) return undefined
  return pairPolicyByChainAndKey
    .get(chainId)
    ?.get(pairCatalogKey(catalogAddressForToken(tokenA), catalogAddressForToken(tokenB)))
}

export function isSwapEnabledForToken(chainId: ChainId | undefined, token: Token): boolean {
  const policy = getTokenPolicy(chainId, token)
  return policy?.swapEnabled !== false
}

export function isSwapEnabledForCurrency(chainId: ChainId | undefined, currency: Currency | undefined): boolean {
  if (!currency || !(currency instanceof Token)) return true
  return isSwapEnabledForToken(chainId, currency)
}

export function isSwapEnabledForPair(chainId: ChainId | undefined, tokenA: Token, tokenB: Token): boolean {
  const pairPolicy = getPairPolicy(chainId, tokenA, tokenB)
  if (pairPolicy && !pairPolicy.swapEnabled) return false
  return isSwapEnabledForToken(chainId, tokenA) && isSwapEnabledForToken(chainId, tokenB)
}

export function isLiquidityEnabledForPair(
  chainId: ChainId | undefined,
  tokenA: Token | undefined,
  tokenB: Token | undefined,
  operation: LiquidityOperation,
): boolean {
  if (!chainId || !tokenA || !tokenB) return true

  const tokenPolicyA = getTokenPolicy(chainId, tokenA)
  const tokenPolicyB = getTokenPolicy(chainId, tokenB)
  if (operation === 'add') {
    if (tokenPolicyA?.addLiquidityEnabled === false || tokenPolicyB?.addLiquidityEnabled === false) {
      return false
    }
  } else if (tokenPolicyA?.removeLiquidityEnabled === false || tokenPolicyB?.removeLiquidityEnabled === false) {
    return false
  }

  const pairPolicy = getPairPolicy(chainId, tokenA, tokenB)
  if (!pairPolicy) return true
  return operation === 'add' ? pairPolicy.addLiquidityEnabled : pairPolicy.removeLiquidityEnabled
}

export function tradeInvolvesSwapDisabledToken(chainId: ChainId | undefined, trade: Trade): boolean {
  if (!chainId) return false
  for (const token of trade.route.path) {
    if (!isSwapEnabledForToken(chainId, token)) return true
  }
  for (const pair of trade.route.pairs) {
    if (!isSwapEnabledForPair(chainId, pair.token0, pair.token1)) return true
  }
  return false
}

export function resolveMaxSwapPriceImpactBps(
  chainId: ChainId | undefined,
  routePairs: Array<{ token0: Token; token1: Token }>,
): number | null {
  if (!chainId || routePairs.length === 0) return null

  let minBps: number | null = null
  for (const pair of routePairs) {
    const policy = getPairPolicy(chainId, pair.token0, pair.token1)
    const bps = policy?.maxSwapPriceImpactBps ?? null
    if (bps == null) continue
    minBps = minBps == null ? bps : Math.min(minBps, bps)
  }
  return minBps
}

export function resolveMaxSwapPriceImpactPercent(
  chainId: ChainId | undefined,
  trade: Trade | undefined,
): Percent {
  if (!trade || !chainId) return BLOCKED_PRICE_IMPACT_NON_EXPERT
  const bps = resolveMaxSwapPriceImpactBps(chainId, trade.route.pairs)
  if (bps == null) return BLOCKED_PRICE_IMPACT_NON_EXPERT
  return new Percent(JSBI.BigInt(bps), JSBI.BigInt(10000))
}

/** Solo tests: resetea registries entre casos. */
export function __resetMarketPolicyRegistryForTests(): void {
  tokenPolicyByChainAndAddress.clear()
  pairPolicyByChainAndKey.clear()
}
